import { addDays, eachDayOfInterval, endOfDay, format, startOfDay, subDays, } from 'date-fns';
import nb from 'date-fns/locale/nb';
import chunk from 'lodash/chunk';
import { useEffect, useState } from 'react';
import { usePrevious } from '../hooks';
import { getDateInTimezone, parseDay } from '../utils';
export default function useWeekRanges({ startDate, endDate, daysAhead, setSelectedDate, setStartDate, setEndDate, selectedDay, }) {
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
    const [weeks, setWeeks] = useState([getWeek(startDate, daysAhead)]);
    const previousRange = usePrevious(daysAhead);
    const firstDayOfWeek = weeks?.[currentWeekIndex][0].date;
    const lastDayOfWeek = endOfDay(addDays(firstDayOfWeek, daysAhead - 1));
    /*
     * Goto next week
     */
    const next = () => {
        const nextIndex = currentWeekIndex + 1;
        const nextWeekInDates = weeks[nextIndex];
        if (nextWeekInDates && nextWeekInDates.length === daysAhead) {
            setEndDate(endOfDay(nextWeekInDates[nextWeekInDates.length - 1].date));
            setSelectedDate(nextWeekInDates[0].formatted.dateString);
            setStartDate(nextWeekInDates[0].date);
            setCurrentWeekIndex(nextIndex);
            return;
        }
        const currentWeek = weeks[currentWeekIndex];
        const nextWeekStart = addDays(currentWeek[currentWeek?.length - 1].date, 1);
        const nextWeek = getWeek(nextWeekStart, daysAhead);
        setCurrentWeekIndex(nextIndex);
        setWeeks([...weeks, nextWeek]);
        setSelectedDate(nextWeek[0].formatted.dateString);
        setStartDate(startOfDay(nextWeek[0].date));
        setEndDate(endOfDay(nextWeek[nextWeek.length - 1].date));
    };
    /*
     * Goto previous week
     */
    const prev = () => {
        const getPrevWeek = () => {
            if (currentWeekIndex === 0) {
                const firstDayOfPrevWeek = subDays(weeks[0][0].date, daysAhead);
                const prevWeek = getWeek(firstDayOfPrevWeek, daysAhead);
                return { nextIndex: 0, prevWeek };
            }
            else {
                const nextIndex = currentWeekIndex - 1;
                return { nextIndex, prevWeek: weeks[nextIndex] };
            }
        };
        const { prevWeek, nextIndex } = getPrevWeek();
        if (nextIndex === 0) {
            setWeeks([prevWeek, ...weeks]);
        }
        setCurrentWeekIndex(nextIndex);
        setSelectedDate(prevWeek[0].formatted.dateString);
        setStartDate(startOfDay(prevWeek[0].date));
        setEndDate(endOfDay(prevWeek[prevWeek.length - 1].date));
    };
    const canGoBack = getDateInTimezone(startDate) > getDateInTimezone(new Date());
    // Used when jumping to a random day - for example using the next-available button.
    const jumpToDate = (date) => {
        const parsedDate = parseDay(date);
        // Don't jump to a date in the past
        if (parsedDate < new Date()) {
            return;
        }
        if (startDate < parsedDate && parsedDate < endDate) {
            setSelectedDate(date);
        }
        else {
            const newWeek = getWeek(parsedDate, daysAhead);
            setWeeks([newWeek]);
            setCurrentWeekIndex(0);
            setSelectedDate(date);
            setStartDate(startOfDay(parsedDate));
            setEndDate(endOfDay(newWeek[newWeek.length - 1].date));
        }
    };
    useEffect(() => {
        /*
         * If it's a mobile view we only want to show 5 days instead of 7
         */
        if (previousRange && previousRange !== daysAhead) {
            const flattened = weeks.slice().flat();
            const chunked = chunk(flattened, daysAhead);
            const nextIndex = chunked.findIndex((week) => week.find((day) => day.formatted.dateString === selectedDay));
            if (chunked[chunked.length - 1].length < daysAhead) {
                const lastWeek = chunked[chunked.length - 1];
                const lastDate = lastWeek[lastWeek.length - 1].date;
                const nextWeek = getWeek(addDays(lastDate, 1), daysAhead - chunked[chunked.length - 1].length);
                chunked[chunked.length - 1].push(...nextWeek);
                setWeeks(chunked);
                setSelectedDate(selectedDay);
                setCurrentWeekIndex(nextIndex);
                setStartDate(chunked[0][0].date);
                /**
                 * This could be a problem if the user keep switching between
                 * mobile and desktop view. It will continually set a new end date
                 * as the chunked array grow.
                 *
                 * Same on line 172
                 */
                setEndDate(lastDayOfWeek);
                return;
            }
            setCurrentWeekIndex(nextIndex);
            setSelectedDate(selectedDay);
            setStartDate(chunked[0][0].date);
            setEndDate(lastDayOfWeek);
            setWeeks(chunked);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [daysAhead]);
    return {
        prev,
        next,
        setCurrentWeekIndex,
        currentWeekIndex,
        weeks,
        jumpToDate,
        canGoBack,
    };
}
/*
 * Create weeks based on daysAhead. The API only returns days with available time slots,
 * so we need to create the days that is not returned by the API.
 */
export const getWeek = (startDate, daysAhead) => {
    return eachDayOfInterval({
        start: startDate,
        end: addDays(startDate, daysAhead - 1),
    })
        .map((day) => ({
        date: day,
        formatted: {
            iso: day.toISOString(),
            dateString: format(day, 'yyyy-MM-dd', { locale: nb }),
            day: format(day, 'dd', { locale: nb }),
            month: format(day, 'MM', { locale: nb }),
            year: format(day, 'yyyy', { locale: nb }),
        },
    }))
        .sort((a, b) => a.date.getTime() - b.date.getTime());
};
